import { __assign, __read, __rest } from "tslib";
import React, { memo, useContext, useEffect, useMemo, useState, } from 'react';
import { Field } from 'react-final-form';
import FieldError from '@mercell/field-error-react';
import { Dropdown } from '@mercell/dropdown-react';
import stringify from 'fast-safe-stringify';
import { FormContext } from '../utils/FormContext';
import { prepareAndToggleFieldErrors } from '../utils/prepareAndToggleFieldErrors';
import { TrackFieldValueByName } from '../utils/TrackFieldValueByName';
import { resolveUseSamePath } from '../utils/resolveUseSamePath';
import { nestedResolveUseSamePath } from '../utils/nestedResolveUseSamePath';
import { nl2br } from '../utils/nl2br';
var UpdateBasedOnParentValue = function (_a) {
    var fieldToTrackExists = _a.fieldToTrackExists, matchedValues = _a.matchedValues, isMulti = _a.isMulti, inputValue = _a.inputValue, onChange = _a.onChange;
    useEffect(function () {
        var matchedValuesLength = matchedValues.length;
        switch (true) {
            case fieldToTrackExists &&
                matchedValuesLength === 0 &&
                isMulti &&
                inputValue.length !== 0:
                onChange([]);
                break;
            case fieldToTrackExists && matchedValuesLength === 0 && inputValue:
                onChange('');
                break;
            case fieldToTrackExists &&
                isMulti &&
                matchedValuesLength > 0 &&
                matchedValuesLength < inputValue.length:
                onChange(matchedValues.map(function (elem) { return elem.value; }));
                break;
            default:
                break;
        }
    }, [fieldToTrackExists, matchedValues, isMulti, onChange, inputValue]);
    return null;
};
export var SelectField = memo(function (_a) {
    var _b, _c, _d;
    var field = _a.field, name = _a.name;
    var _e = field, _f = _e.fieldWrapperProperties, fieldWrapperProperties = _f === void 0 ? {} : _f, _g = _e.elementProperties, elementProperties = _g === void 0 ? {} : _g, _h = _e.fieldLabel, fieldLabel = _h === void 0 ? function () { return null; } : _h, optionsList = _e.optionsList, _j = _e.ariaRequired, ariaRequired = _j === void 0 ? false : _j, type = _e.type, validations = _e.validations, fieldToTrack = _e.fieldToTrack, disabled = _e.disabled, associatedValidationBasedOnOtherFieldValues = _e.associatedValidationBasedOnOtherFieldValues, arrayOfFieldsToRevalidateOnChange = _e.arrayOfFieldsToRevalidateOnChange, _k = _e.partialFieldProps, partialFieldProps = _k === void 0 ? {} : _k, restProps = __rest(_e, ["fieldWrapperProperties", "elementProperties", "fieldLabel", "optionsList", "ariaRequired", "type", "validations", "fieldToTrack", "disabled", "associatedValidationBasedOnOtherFieldValues", "arrayOfFieldsToRevalidateOnChange", "partialFieldProps"]);
    var selectProperties = elementProperties;
    var _l = useContext(FormContext), validationCallback = _l.validationCallback, showErrorsOnInitialRender = _l.showErrorsOnInitialRender;
    var _m = __read(useState(false), 2), reApplyRequiredIndicator = _m[0], setReApplyRequiredIndicator = _m[1];
    var validateMethod = function () { return null; };
    var trackedFieldValue;
    var updatedConditions = nestedResolveUseSamePath((_c = (_b = associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.conditionalFields) === null || _b === void 0 ? void 0 : _b.conditions) !== null && _c !== void 0 ? _c : [], name);
    var updatedAssociatedValidationBasedOnOtherFieldValues = {
        logicalOperator: (_d = associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.conditionalFields) === null || _d === void 0 ? void 0 : _d.logicalOperator,
        conditions: updatedConditions,
    };
    if ((validations || associatedValidationBasedOnOtherFieldValues) &&
        validationCallback) {
        validateMethod = function (fieldValue, allValues) {
            return validationCallback(fieldValue, validations, allValues, {
                conditionalFields: updatedAssociatedValidationBasedOnOtherFieldValues,
                rules: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.rules,
                message: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.message,
                reApplyRequiredIndicator: associatedValidationBasedOnOtherFieldValues === null || associatedValidationBasedOnOtherFieldValues === void 0 ? void 0 : associatedValidationBasedOnOtherFieldValues.reApplyRequiredIndicator,
            }, setReApplyRequiredIndicator);
        };
    }
    if (fieldToTrack && fieldToTrack.fieldName) {
        var fieldName = fieldToTrack.fieldName, useSamePath = fieldToTrack.useSamePath;
        trackedFieldValue = TrackFieldValueByName(name, fieldName, useSamePath);
    }
    var updatedOptions = useMemo(function () { return optionsList(trackedFieldValue); }, [optionsList, trackedFieldValue]);
    var validateFields = arrayOfFieldsToRevalidateOnChange === null || arrayOfFieldsToRevalidateOnChange === void 0 ? void 0 : arrayOfFieldsToRevalidateOnChange.map(function (fieldToRevalidate) {
        if (fieldToRevalidate.useSamePath) {
            return resolveUseSamePath(name, fieldToRevalidate.fieldName);
        }
        return fieldToRevalidate.fieldName;
    });
    var _o = partialFieldProps.parse, parse = _o === void 0 ? function (val) { return val; } : _o;
    return (React.createElement("div", __assign({}, fieldWrapperProperties),
        fieldLabel((selectProperties === null || selectProperties === void 0 ? void 0 : selectProperties.id) || name, reApplyRequiredIndicator),
        React.createElement(Field, __assign({}, partialFieldProps, { parse: parse, name: name, type: type, validate: validateMethod, validateFields: validateFields }), function (_a) {
            var input = _a.input, meta = _a.meta;
            var _b = prepareAndToggleFieldErrors(meta, showErrorsOnInitialRender), showErrors = _b.showErrors, errors = _b.errors;
            var value = updatedOptions === null || updatedOptions === void 0 ? void 0 : updatedOptions.filter(function (option) {
                return restProps.isMulti
                    ? input.value.includes(option.value)
                    : option.value === input.value;
            });
            var onChange = function (onChangeValue) {
                if (restProps.isMulti) {
                    if (onChangeValue &&
                        onChangeValue.length) {
                        input.onChange(onChangeValue.map(function (entry) { return entry.value; }));
                    }
                    else
                        input.onChange([]);
                }
                else if (onChangeValue) {
                    input.onChange(onChangeValue
                        .value);
                }
                else {
                    input.onChange('');
                }
            };
            return (React.createElement(React.Fragment, null,
                React.createElement(UpdateBasedOnParentValue, { fieldToTrackExists: !!fieldToTrack, matchedValues: value, isMulti: restProps.isMulti, inputValue: input.value, onChange: input.onChange }),
                React.createElement(Dropdown, __assign({}, input, selectProperties, restProps, { isDisabled: selectProperties.disabled || disabled, options: updatedOptions, id: (selectProperties === null || selectProperties === void 0 ? void 0 : selectProperties.id) || name, className: selectProperties === null || selectProperties === void 0 ? void 0 : selectProperties.className, "aria-required": ariaRequired, invalid: showErrors, value: value, onChange: onChange })),
                showErrors && (React.createElement(FieldError, null, nl2br(errors)))));
        })));
}, function (prevProps, nextProps) {
    try {
        return stringify(prevProps) === stringify(nextProps);
    }
    catch (e) {
        console.warn("SelectField: ".concat(e));
        return false;
    }
});
SelectField.displayName = 'SelectField';
